import { useCallback, useRef } from "react";
import { AsyncProps } from "react-select/async";

/**
 * Aplica debounce ao callback `loadOptions` de um componente AsyncSelect do react-select.
 *
 * Adia a execução do `loadOptions` em 500ms após a última tecla pressionada,
 * evitando uma nova requisição à API a cada caractere digitado.
 *
 * Compatível com os dois estilos de assinatura do `loadOptions` do react-select:
 * callback e Promise.
 *
 * @param loadOptions - A prop `loadOptions` original recebida via `AsyncProps`.
 * @returns Uma versão com debounce de `loadOptions`, pronta para ser passada ao `AsyncSelect`.
 *
 * @example
 * const debouncedLoadOptions = useDebounceLoadOptions(props.loadOptions);
 * // <AsyncSelect loadOptions={debouncedLoadOptions} />
 */
export function useDebounceLoadOptions(
  loadOptions: AsyncProps<any, any, any>["loadOptions"]
) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  return useCallback(
    (inputValue: string, callback: (options: any) => void) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const result = loadOptions?.(inputValue, callback);
        if (result && typeof (result as Promise<any>).then === "function") {
          (result as Promise<any>).then(callback);
        }
      }, 1500);
    },
    [loadOptions]
  );
}
